import { Link } from "react-router-dom";
import { CheckCircle2, FileCheck2, Download } from "lucide-react";
import { BOTANICAL_EXTRACTS, IMAGES } from "@/lib/company";

export default function BotanicalExtracts() {
  return (
    <div data-testid="botanical-extracts-page">
      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.turmeric} alt="Botanical extracts" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 py-24 w-full">
          <span className="inline-block px-4 py-1.5 bg-brand-gold text-brand-green-dark text-[11px] font-bold uppercase tracking-[0.25em] rounded-sm mb-5">Category A</span>
          <h1 className="font-display font-bold text-white text-3xl md:text-5xl lg:text-6xl leading-tight max-w-4xl">
            Standardized Botanical Extracts —<br />
            <span className="text-brand-gold">Pharmaceutical Grade, Globally Compliant</span>
          </h1>
          <p className="mt-6 text-white/85 text-lg max-w-3xl">
            High-potency herbal extracts manufactured under WHO-GMP and ISO 22000:2018 standards, tested by HPLC and UV spectrophotometry, and delivered with full traceability documentation.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-brand-charcoal/85 leading-relaxed text-lg">
            Our botanical extracts are sourced from India's most reputable extraction facilities — each holding WHO-GMP, ISO 22000:2018, and HACCP certifications. Every batch undergoes rigorous in-house testing using HPLC, UV Spectrophotometer, TLC, GC, and pH analysis, followed by third-party verification. Whether you are formulating dietary supplements, functional foods, or pharmaceutical preparations, our extracts provide the standardization, consistency, and regulatory documentation your quality team demands.
          </p>
        </div>
      </section>

      {/* Product cards */}
      <section className="py-16 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 space-y-10">
          {BOTANICAL_EXTRACTS.map((p, idx) => (
            <div
              key={p.slug}
              data-testid={`extract-detail-${p.slug}`}
              className={`bg-white rounded-lg shadow-md border border-neutral-200 overflow-hidden grid lg:grid-cols-2 gap-0 ${idx % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""}`}
            >
              <div className="aspect-[4/3] lg:aspect-auto lg:h-full overflow-hidden bg-neutral-100">
                <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-8 md:p-10 flex flex-col">
                <div className="font-accent italic text-brand-gold text-sm md:text-base">{p.tagline}</div>
                <h2 className="mt-2 font-display font-bold text-brand-charcoal text-3xl">{p.name}</h2>
                <div className="mt-1 text-brand-charcoal/60 text-sm italic">{p.botanical}</div>

                <p className="mt-5 text-brand-charcoal/80 leading-relaxed text-sm">{p.description}</p>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  {p.specs.map((s) => (
                    <div key={s.k} className="flex items-start gap-2 text-xs">
                      <CheckCircle2 className="w-3.5 h-3.5 text-brand-green mt-0.5 shrink-0" />
                      <div>
                        <span className="font-semibold text-brand-charcoal">{s.k}:</span>{" "}
                        <span className="text-brand-charcoal/75">{s.v}</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-6 space-y-3 text-sm">
                  <div>
                    <span className="text-xs uppercase tracking-widest text-brand-gold font-bold">Applications</span>
                    <div className="mt-1 text-brand-charcoal/85">{p.applications}</div>
                  </div>
                  <div>
                    <span className="text-xs uppercase tracking-widest text-brand-gold font-bold">Packaging</span>
                    <div className="mt-1 text-brand-charcoal/85">{p.packaging}</div>
                  </div>
                </div>

                <div className="mt-7 flex flex-wrap gap-3">
                  <Link to="/contact" data-testid={`spec-sheet-${p.slug}`} className="btn-green" style={{ padding: "0.7rem 1.2rem", fontSize: "0.9rem" }}>
                    Request Spec Sheet
                  </Link>
                  <Link to="/contact" data-testid={`order-sample-${p.slug}`} className="btn-outline-green" style={{ padding: "0.7rem 1.2rem", fontSize: "0.9rem" }}>
                    Order Sample
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* QA strip */}
      <section className="py-20 bg-brand-green text-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-gold">Documentation</span>
            <h2 className="mt-3 font-display font-bold text-3xl md:text-4xl gold-underline-center">
              Every Extract Includes Complete Documentation
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              "Batch-specific Certificate of Analysis (CoA)",
              "Heavy metals & microbial testing report",
              "Manufacturer GMP certificate reference",
              "Material Safety Data Sheet (MSDS)",
              "Non-GMO, Allergen-Free, BSE/TSE-Free declarations",
              "Halal / Kosher / Organic certificates (where applicable)",
            ].map((d) => (
              <div key={d} className="flex items-start gap-3 p-4 bg-white/10 backdrop-blur-sm rounded-md border border-white/15">
                <FileCheck2 className="w-5 h-5 text-brand-gold shrink-0 mt-0.5" />
                <span className="text-sm">{d}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/contact" data-testid="download-sample-coa" className="btn-gold">
              <Download className="w-4 h-4" /> Download Sample CoA
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
